import React, { useRef, useState } from 'react'
import ThemeInterface from '@/theme-interface'
import { makeStyles, useTheme } from '@mui/styles'
import { useDispatch } from 'react-redux'
import { OrgamaxReduxState } from '@/redux/state'
import { ButtonIconSquare, ButtonSquare, CancelButton, SquareIconButton } from '@/modules/base/components/buttons'
import lang from '@/lang'
import PanelFooter from '@/components/layouts/panel/panel-footer'
import { ReactNodeLike } from 'prop-types'
import { saveDocument } from '@/redux/ducks/document/documentDrawerHelpers'
import useCurrentModuleContext from '@/modules/_helpers/accessCheck/useCurrentModuleContext'
import { initDeletion } from '@/redux/ducks/document/list'
import IconDelete from '@assets/images/icons/trashcan.svg'
import ShareIcon from '@mui/icons-material/Share'
import { isEqual } from 'lodash'
import { useOpenModal } from '@/modules/_helpers/openModal'
import { Title } from '@/modules/base/components/texts'
import { NumberInput } from '@/modules/base/components/inputs'
import { user } from 'ampersand-app' // FLAG:useDocuAi — read the per-tenant experimental flag
import FileService from '@/modules/file/fileService'
import { useAppSelector } from '@/redux/store'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { Document } from '@/modules/document/types'
import { mimeTypes, experimentalFeatures } from '@/modules/base/util/constants'
import { isEmbeddedInvoiceReview } from '@/modules/document/utils/invoice-analysis-state'
import { isDesktopOwnedFile } from '@/modules/file/desktop-owned-file'
import { useUpdateSearchParams } from '@/modules/_helpers/updateSearchParams'
import { closeDocumentDrawer } from '@/redux/ducks/document/drawer'
import ReportAnalysisProblemButton from '@/modules/document/components/review/report-analysis-problem-button'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  content: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 30,
  },
  buttonWrapper: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
  },
  panelFooter: {
    backgroundColor: theme.backgroundColor,
  },
  modalWrapper: {
    padding: 22,
  },
  button: {
    width: 145,
  },
  numberInput: {
    width: 145,
  },
  title: {
    textAlign: 'left',
  },
}))

type ComparableValue = string | number | boolean | null | undefined | Record<string, unknown> | unknown[]

const hasValueChanged = (newValue: ComparableValue, oldValue: ComparableValue): boolean => {
  // If both are empty (null, undefined, or empty string), consider them equal
  if ((!newValue || newValue === '') && (!oldValue || oldValue === '')) {
    return false
  }
  // If one is empty and the other isn't, there's a change
  if ((!newValue || newValue === '') !== (!oldValue || oldValue === '')) {
    return true
  }
  // For non-empty values, use regular isEqual
  return !isEqual(newValue, oldValue)
}

// FLAG:useDocuAi — `skipInvoiceDetails`: for a document whose Details tab hosts the central review,
// documentInvoiceDetails is the review's data, and saveDocument leaves it out of the PUT. Counting
// it here would promise a save this button no longer performs ("Speichern" that only closes).
const hasDocumentChanged = (newDoc: Document, oldDoc: Document, skipInvoiceDetails = false): boolean => {
  if (!newDoc || !oldDoc) return false

  // Check top-level document properties
  for (const key in newDoc) {
    if (key === 'documentInvoiceDetails') continue // Handle this separately
    if (hasValueChanged(newDoc[key], oldDoc[key])) {
      return true
    }
  }

  // Check documentInvoiceDetails
  if (!skipInvoiceDetails && (newDoc.documentInvoiceDetails || oldDoc.documentInvoiceDetails)) {
    const newDetails = newDoc.documentInvoiceDetails || {}
    const oldDetails = oldDoc.documentInvoiceDetails || {}

    for (const key in newDetails) {
      if (hasValueChanged(newDetails[key], oldDetails[key])) {
        return true
      }
    }
  }

  return false
}

interface DocumentDrawerFooterProps {
  children: ReactNodeLike,
  refreshList: () => void,
  // FLAG:useDocuAi — imperative handle of the drawer-embedded central review (see
  // document-invoice-review-drawer-tab.tsx); Save delegates to it while that tab is active.
  reviewSaveRef?: React.RefObject<{ onSave: () => void }>,
}

const DocumentDrawerFooter = (props: DocumentDrawerFooterProps) => {
  const dispatch = useDispatch()
  const classes = useStyles(props)
  const theme = useTheme()
  const openModal = useOpenModal()
  const [shareDays, setShareDays] = useState(7)
  // ref cause of closure problem
  const shareDaysRef = useRef(shareDays)

  const useDocuAiEnabled = user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)

  const { currentCollaboration, isReadOnly } = useAppSelector((state) => state.document.list)
  const { rights } = useCurrentModuleContext()

  const [, updateSearchParams] = useUpdateSearchParams()

  const {
    children,
    refreshList,
    reviewSaveRef,
  } = props

  const {
    document,
    initialDocument,
    folderReadOnly,
    activeTab,
  } = useAppSelector((state: OrgamaxReduxState) => state.document.drawer)

  // FLAG:useDocuAi — while the Details tab hosts the embedded central review (pending-review
  // invoice, tab index 1 is active), Save must trigger the review save — the legacy drawer
  // save would mark the document reviewed WITHOUT the review payload. Validity comes from the
  // review form's own state. flag-OFF: isEmbeddedInvoiceReview is only reachable flag-ON
  // (pending review requires a DocuAI analysis), so the legacy save path is untouched.
  const { isFormValid: reviewFormValid } = useAppSelector((state) => state.receipt.edit)
  const reviewOwnsInvoiceDetails = !!useDocuAiEnabled && isEmbeddedInvoiceReview(document, folderReadOnly)
  const embeddedReviewSave = reviewOwnsInvoiceDetails && activeTab === 1

  // A desktop-owned document never posts documentInvoiceDetails either (only notes and tags reach
  // the API), so the legacy details tab's own prefill must not turn „Schließen" into „Speichern".
  const hasChanges = hasDocumentChanged(document, initialDocument, reviewOwnsInvoiceDetails || isDesktopOwnedFile(document))

  const deleteHidden = initialDocument?.isAssigned || initialDocument?.linkedTodoFiles.length > 0 || initialDocument.externalOwnerSystem

  const deleteEnabled = rights.write && !currentCollaboration

  const shareHidden = document?.mimetype === mimeTypes.MIMETYPE_PDF

  const onRemove = () => {
    if (!deleteEnabled || folderReadOnly) return
    dispatch(initDeletion([document]))
  }

  const onModalSubmit = (close) => {
    const currentDays = shareDaysRef.current

    FileService.getDownloadLink({ minioId: document.minioId, days: currentDays, context: 'file' }).then(res => {
      navigator.clipboard.writeText(res.data.data.downloadUrl).then(() => {
        toast.showToast({ type: 'success', message: lang.documents.shareHUBLinkCopied })
        close()
      }).catch(() => {
        toast.showToast({ type: 'error' })
      })
    })
  }

  const handleShare = () => {
    if (isReadOnly || folderReadOnly) return
    openModal(close => ({
      component: 'ModalSmall',
      children: <div className={classes.modalWrapper}>
        <Title className={classes.title} position='center'>{lang.documents.drawer.footer.createDownloadLink}</Title>
        <NumberInput
          showButtons
          min={1}
          max={30}
          className={classes.numberInput}
          useFormik={false}
          name='days'
          precision={0}
          label={lang.documents.drawer.footer.dayCount}
          value={shareDays}
          onChange= {(number) => {
            const val = Number(number)
            setShareDays(val)
            shareDaysRef.current = val
          }}
        />
      </div>,
      props: {
        onBackdropClick: close,
        showCloseIcon: false,
        actions: [
          <CancelButton type='cancel' label={lang.cancelButtonLabel} onClick={close} className={classes.button} />,
          <ButtonSquare
            className={classes.button}
            onClick={() => { onModalSubmit(close) }}
            label={lang.generics.make}
          />,
        ],
      },
    }))
  }

  // FLAG:useDocuAi — only the review tab itself confirms a review. A pending review used to label
  // this button "Speichern" on EVERY tab and complete the review from there — which booked an
  // Ausgabe the operator never asked for (the booking target is picked in the review, not here).
  // embeddedReviewSave is the one case that still says "Speichern" without a field edit: the
  // Details tab IS the review then, and this button submits it — labelling that "Schließen" while
  // it saves would be a lie.
  const buttonLabel = (hasChanges || embeddedReviewSave)
    ? lang.saveButtonLabel
    : lang.closeButtonLabel

  const onClose = () => {
    refreshList?.()
    updateSearchParams([{ key: 'documentId' }, { key: 'rowIndex' }])
    dispatch(closeDocumentDrawer())
  }

  return (
    <PanelFooter className={classes.panelFooter}>
      <div className={classes.content}>
        <div className={classes.buttonWrapper}>
          {!deleteHidden && <SquareIconButton
            onClick={onRemove}
            icon={IconDelete}
            disabled={!deleteEnabled || isReadOnly || folderReadOnly}
            size='large'
            iconColor={theme.black}
            variant={'white'} />}
          {!shareHidden && <SquareIconButton
            tooltip={lang.documents.shareHUBLinkTooltip}
            onClick={handleShare}
            icon={ShareIcon}
            disabled={isReadOnly || folderReadOnly}
            size='large'
            iconColor={theme.black}
            variant={'white'} />}
          {/* FLAG:docuAiEnhancements — gate lives inside the component, shared with the expense
              review surface so both entry points cannot drift apart. */}
          <ReportAnalysisProblemButton minioId={document.minioId} />
        </div>
        {children}
        <ButtonIconSquare
          label={buttonLabel}
          // The review confirmation lives in the review tab alone (embeddedReviewSave); every other
          // tab saves the file's own data and leaves documentInvoiceDetails — and therefore the
          // Ausgabe/Einnahme it would book — untouched (see saveDocument).
          onClick={() => (embeddedReviewSave ? reviewSaveRef?.current?.onSave() : saveDocument(onClose))}
          // F16.5 — the collaboration read-only lock applies in the embedded-review branch too:
          // a read-only collaborator must not see an active Speichern on a pending review.
          disabled={embeddedReviewSave ? (!reviewFormValid || isReadOnly) : (isReadOnly || folderReadOnly)}
          variant={'fill'} />
      </div>
    </PanelFooter>
  )
}

export default DocumentDrawerFooter
